
import Page from '@/views/Page.vue';
import { Component, Mixins } from 'vue-property-decorator';
import { orgAdminStore } from '@/store';
import { DataTableHeader, DataOptions } from 'vuetify';
import { VuetifyMixin, MindsetDisplayMixin, OrgResolverMixin, BAIconsMixin, StringsMixin } from '@/mixins';
import OrgCard from '@/components/orgAdmin/OrgCard.vue';
import OrgAdminLinksCard from './OrgAdminLinksCard.vue';
import { StaffMember, StaffMemberEx, PopulateStaffMemberFromStaffMemberEx } from '@/models/team/StaffMember';
import OrganizationCreateStaffInfo from '@/components/admin/OrganizationCreateStaffInfo.vue';
import { OrganizationModel } from '@/models/organization/OrganizationModel';
import { CoachProfileModel, TeamModel } from '@/models';
import { organizationApi } from '@/api/OrganizationApi';
import { coachApi } from '@/api/CoachApi';
import * as Routes from '@/../types/constants/web_client_user.routes';
import { getEnumKeyByValue } from "@/../types/enums/get-enum-key-by-value";
import { StaffRole } from '@/../types/enums';
import { capitalizeCamelCase } from '@/pipes';
import CoachMindsetReportCard from "@/components/profile/coach/CoachMindsetReportCard.vue"

@Component({
	components: {
		Page,
		OrgCard,
		OrgAdminLinksCard,
		OrganizationCreateStaffInfo,
		CoachMindsetReportCard,
	}
})
export default class OrgAdminManageStaff extends Mixins(OrgResolverMixin, VuetifyMixin, MindsetDisplayMixin, BAIconsMixin, StringsMixin){
	async created() {
		await this.loadOrgStaff();
		await this.loadTeamStaff();
	}

	getEnumKeyByValue = getEnumKeyByValue;

	tableOptions: DataOptions = {
		page: 1,
		itemsPerPage: 100,
		sortBy: ['none'],
		sortDesc: [false],
		groupBy: [],
		groupDesc: [false],
		multiSort: false,
		mustSort: false,
	};

	get TableLoading(): boolean{
		return !orgAdminStore.loadOrganizationStaffInitialized || orgAdminStore.loadOrganizationStaffLoading;
	}

	async loadOrgStaff(): Promise<void>{
		await this.loadCurrentOrganization();
		return orgAdminStore.loadOrganizationStaff({organizationId: this.organizationId});
	}

	showConfirmDeleteStaff: boolean = false;
	selectedDeleteStaffMember: StaffMember | null = null;
	get ConfirmDeleteText(): string{
		if(this.selectedDeleteStaffMember === null) return "";
		return `Remove "${this.selectedDeleteStaffMember.firstName} ${this.selectedDeleteStaffMember.lastName}" from ${this.CurrentOrganization.name}?`; 
	}
	async confirmDeleteStaff(staff: StaffMember) {
		this.selectedDeleteStaffMember = staff;
		this.showConfirmDeleteStaff = true;
	}
	get RemoveStaffLoading(): boolean{
		return orgAdminStore.removeStaffLoading;
	}
	async deleteStaff(staff: StaffMember){
		await orgAdminStore.removeStaff({ 
			organizationId: this.organizationId,
			staffId: staff.id
		});
		this.showConfirmDeleteStaff = false;
		this.selectedDeleteStaffMember = null;
		await this.loadOrgStaff();
	}

	showConfirmAddStaff: boolean = false;
	addStaffFormValue = this.resetAddStaffForm();
	private resetAddStaffForm(){
		return this.addStaffFormValue = {
			valid: false,
			staffMembers: [],
			sendStaffInvitations: true,
		};
	}
	confirmAddStaff(): void{
		this.resetAddStaffForm();
		this.showConfirmAddStaff = true;
	}
	closeAddStaff(): void{
		this.showConfirmAddStaff = false;
		this.resetAddStaffForm();
	}
	get AddStaffLoading(): boolean{
		return orgAdminStore.patchOrganizationLoading;
	}
	async submitAddStaff(){
		const orgCopy: OrganizationModel = this.CurrentOrganization.copy();
		orgCopy.staffMembers.push(...this.addStaffFormValue.staffMembers);
		await orgAdminStore.patchOrganization(orgCopy, { inviteStaff: this.addStaffFormValue.sendStaffInvitations });
		await this.loadOrgStaff();
		this.showConfirmAddStaff = false;
		this.resetAddStaffForm();
	}

	StaffHeaders: DataTableHeader<any>[] = [
		{ text: 'Name', value: 'name', sortable: false },
		{ text: 'Title', value: 'title', sortable: false },
		{ text: 'Email', value: 'email', sortable: false },
		{ text: 'Mindset', value: 'mindset', sortable: false },
		{ text: '', value: 'actions', sortable: false },
	];
	TeamStaffHeaders: DataTableHeader<any>[] = [
		{ text: 'Name', value: 'name', sortable: false },
		{ text: 'Team', value: 'team', sortable: false },
		{ text: 'Role', value: 'role', sortable: false },
		{ text: 'Email', value: 'email', sortable: false },
		{ text: 'Mindset', value: 'mindset', sortable: false },
		{ text: '', value: 'actions', sortable: false },
		{ text: '', value: 'data-table-expand', sortable: false},
	];
	
	get TeamList(): Array<TeamModel> {
		return orgAdminStore.teamList;
	}
	get StaffList(): Array<StaffMemberEx>{
		return orgAdminStore.staffListEx;
	}

	showCoachLinkDialog: boolean = false;
	isEditingStaffMember: boolean = false;
	editStaffMember: StaffMemberEx;
	editStaffMemberIndex: number = -1;
	coachLink: CoachProfileModel;
	coachLinkSearch: string = ""
	isCoachLinkSelected: boolean = false;
	isCoachLinkWarning: boolean = false;
	onCoachLinkAccountRequested(staffMember: StaffMemberEx) {
		this.isEditingStaffMember = true;
		this.isCoachLinkSelected = false;
		this.isCoachLinkWarning = false;
		this.editStaffMember = new StaffMemberEx().load(staffMember);
		this.coachLinkSearch = this.editStaffMember.lastName.toString();
		const organization = this.CurrentOrganization;
		this.editStaffMemberIndex = organization.staffMembers.findIndex(s => s.email === staffMember.email);
		this.showCoachLinkDialog = true;
	}
	onCoachLinkChange(coach: CoachProfileModel) {
		this.isCoachLinkSelected = false;
		this.coachLink = coach;
		this.isCoachLinkSelected = this.IsNotEmpty(this.coachLink);

		this.isCoachLinkWarning = this.isCoachLinkSelected && ((this.coachLink.firstName != this.editStaffMember.firstName) || (this.coachLink.lastName != this.editStaffMember.lastName));
	}
	async onCoachLinkAccept() {
		this.editStaffMember.loadFromCoach(this.coachLink);
		if( this.IsNotEmpty(this.editStaffMember) && (this.editStaffMemberIndex >= 0)) {
			const organization = this.CurrentOrganization;
			const index = this.editStaffMemberIndex;
			Object.assign(organization.staffMembers[index], this.editStaffMember);
			PopulateStaffMemberFromStaffMemberEx(organization.staffMembers[index], this.editStaffMember);
			await organizationApi.patch({id: organization.id, staffMembers: organization.staffMembers});
		}
		this.editStaffMember = undefined;
		this.coachLink = undefined;
		this.showCoachLinkDialog = false;
		this.isEditingStaffMember = false;
		await this.loadOrgStaff();
	}
	onCoachLinkCancel() {
		this.showCoachLinkDialog = false;
		this.coachLink = undefined;
		this.editStaffMember = undefined;
		this.isEditingStaffMember = false;
	}
	onCoachLinkAccountToMatch(staffMember: StaffMemberEx) {
		this.isEditingStaffMember = true;
		this.isCoachLinkSelected = false;
		this.isCoachLinkWarning = false;
		this.editStaffMember = new StaffMemberEx().load(staffMember);
		const organization = this.CurrentOrganization;
		this.editStaffMemberIndex = organization.staffMembers.findIndex(s => s.email === staffMember.email);
		this.onCoachLinkChange(staffMember.match);
		this.showCoachLinkDialog = true;
	}

	onMinsetErrorAcknowledge(staffMember: StaffMemberEx) {
	}

	isLoadingTeamStaff: boolean = false;
	teamStaff: Array<StaffMemberEx>;
	async loadTeamStaff() {
		this.isLoadingTeamStaff = true;
		const staffMap = new Map<string, StaffMemberEx>();

		await this.loadOrgTeams(this.organizationId);
		this.TeamList.flatMap(team => team.staffMembers).forEach(staff => {
			const id = staff.coachId || staff.email;
			if( !staffMap.has(id)) {
				const staffEx: StaffMemberEx = new StaffMemberEx().load(staff);
				staffMap.set(id, staffEx);
			}
		})
		this.teamStaff = Array.from(staffMap.values());
		for( const staff of this.teamStaff ) {
			const coach = await coachApi.findById(staff.coachId)
			staff.loadFromCoach(coach);
			staff.coach = coach;
			await staff.loadTeams(this.TeamList);
		}
		this.isLoadingTeamStaff = false;
	}
	getStaffRole(role: string) {
		const key: string = getEnumKeyByValue(StaffRole, role);
		return capitalizeCamelCase(key);
	}
	gotoTeam(teamId: string) {
		this.$router.push({
			name: Routes.TeamDashboard,
			params:{
				teamId,
			}			
		})
	}

}
